/**
 * app-config.ts — Database-driven configuration lists
 *
 * All config is stored in Firestore under `appConfig/{key}`.
 * On first read, defaults are seeded automatically so the app works
 * out-of-the-box. Super Admin can edit lists without a deploy.
 *
 * Cache: per-session in-memory so Firestore is hit once per page load.
 */

import { db } from "@/lib/firebase";
import {
  doc, getDoc, setDoc, collection, getDocs, query, where, Timestamp,
} from "firebase/firestore";
import { GENRES, normalizeGenre } from "@/lib/genres";
import { DEFAULT_CONTRIBUTOR_ROLES } from "@/data/contributor-roles";

// ─── Types ─────────────────────────────────────────────────────────────────

export interface AppConfigDoc {
  items: string[];
  updatedAt?: Timestamp | string;
}

export interface GenreConfigItem {
  id: string;
  label: string;
  emoji: string;
}

export interface GenreConfigDoc {
  items: GenreConfigItem[];
  updatedAt?: Timestamp | string;
}

// ─── Defaults (seeds on first use) ─────────────────────────────────────────

// Moved to a dependency-free module so server code (BB Copilot) can read the
// same list without pulling in the browser Firebase SDK.
export { DEFAULT_CONTRIBUTOR_ROLES };

export const DEFAULT_CREATOR_ROLES: string[] = [
  "Artist", "Singer", "Rapper", "MC", "Vocalist",
  "DJ", "Turntablist", "Performer",
  "Producer", "Beat Maker", "Remixer",
  "Songwriter", "Composer", "Lyricist", "Arranger",
  "Recording Engineer", "Mixing Engineer", "Mastering Engineer",
  "Musician", "Session Musician", "Instrumentalist",
  "Label", "Publisher", "A&R", "Manager", "Music Supervisor",
  "Graphic Artist", "Photographer", "Art Director",
  "Video Director", "Video Editor", "Cinematographer", "Colorist", "VFX Artist",
  "Print on Demand", "Other",
];

export const DEFAULT_INSTRUMENTS: string[] = [
  // Strings
  "Acoustic Guitar", "Electric Guitar", "Bass Guitar", "Classical Guitar",
  "12-String Guitar", "Slide Guitar", "Lap Steel Guitar",
  "Violin", "Viola", "Cello", "Double Bass", "Upright Bass",
  "Banjo", "Mandolin", "Ukulele", "Harp", "Sitar", "Bouzouki", "Oud",
  // Keyboards
  "Piano", "Grand Piano", "Electric Piano", "Organ", "Hammond Organ",
  "Synthesizer", "Synth", "Mellotron", "Harpsichord", "Clavinet",
  "Wurlitzer", "Rhodes", "Keytar",
  // Drums & Percussion
  "Drums", "Drum Kit", "Electronic Drums", "Drum Machine",
  "Percussion", "Congas", "Bongos", "Djembe", "Cajon", "Tambourine",
  "Maracas", "Shaker", "Cowbell", "Triangle", "Glockenspiel",
  "Marimba", "Xylophone", "Vibraphone", "Tabla", "Timbales",
  // Brass & Woodwinds
  "Trumpet", "Flugelhorn", "Trombone", "French Horn", "Tuba",
  "Alto Saxophone", "Tenor Saxophone", "Baritone Saxophone", "Soprano Saxophone",
  "Clarinet", "Flute", "Oboe", "Bassoon", "Harmonica",
  // Voice
  "Lead Vocals", "Backing Vocals", "Choir", "Beatbox",
  // Electronic
  "DJ / Turntables", "Drum Machine", "Sampler", "Theremin", "Vocoder",
  "Electronic Pads", "Launchpad", "DAW Production",
  // Other
  "Bagpipes", "Accordion", "Didgeridoo", "Steel Pan", "Other",
];

export const DEFAULT_PUBLISHERS: string[] = [
  "Self-Published", "Independent",
];

/** Sync Licensing catalog genres — placement/mood-oriented, distinct from the
 *  main music-catalog genre taxonomy (buyers search "Cinematic"/"Corporate",
 *  not "Hip-Hop"). Database-driven like every other list here — Super Admin
 *  or any user can grow it via FieldAutocomplete's "+ Save to library".
 *  Gordon 2026-09-30: "add genre selection in this page and listing". */
export const DEFAULT_SYNC_GENRES: string[] = [
  "Cinematic", "Film & TV Score", "Trailer / Epic", "Corporate", "Uplifting",
  "Dramatic", "Dark / Suspense", "Horror", "Comedy", "Sports / Action",
  "Documentary", "Nature / Travel", "Holiday / Seasonal", "Kids / Family",
  "Romance", "Orchestral", "Acoustic / Folk", "Ambient", "World",
  "Hip-Hop", "Electronic", "Jazz", "Rock", "Pop", "Classical", "Reggae",
  "Latin", "Country", "Blues", "Funk / Soul", "Gospel", "Other",
];

export const DEFAULT_COPYRIGHT_SOCIETIES: string[] = [
  "ASCAP", "BMI", "SESAC", "SOCAN", "PRS (UK)", "GEMA (Germany)",
  "SACEM (France)", "APRA AMCOS (Australia/NZ)", "STIM (Sweden)",
  "KODA (Denmark)", "TONO (Norway)", "Teosto (Finland)", "SAMI (Sweden)",
  "SABAM (Belgium)", "BUMA/STEMRA (Netherlands)", "SUISA (Switzerland)",
  "JASRAC (Japan)", "KOMCA (Korea)", "CASH (Hong Kong)", "Other", "None",
];

export const DEFAULT_GENRES: GenreConfigItem[] = [
  { id: "hip-hop",    label: "Hip-Hop",           emoji: "🎤" },
  { id: "rnb",        label: "R&B / Soul",         emoji: "🎷" },
  { id: "electronic", label: "Electronic",         emoji: "🎛️" },
  { id: "jazz",       label: "Jazz",               emoji: "🎺" },
  { id: "reggae",     label: "Reggae / Dancehall", emoji: "🌴" },
  { id: "rock",       label: "Rock",               emoji: "🎸" },
  { id: "pop",        label: "Pop",                emoji: "🌟" },
  { id: "classical",  label: "Classical",          emoji: "🎻" },
  { id: "gospel",     label: "Gospel",             emoji: "🙏" },
  { id: "afrobeats",  label: "Afrobeats",          emoji: "🥁" },
  { id: "latin",      label: "Latin",              emoji: "💃" },
  { id: "country",    label: "Country / Folk",     emoji: "🤠" },
  { id: "blues",      label: "Blues",              emoji: "🎵" },
  { id: "world",      label: "World Music",        emoji: "🌍" },
  { id: "punk",       label: "Punk / Alternative", emoji: "⚡" },
  { id: "metal",      label: "Metal",              emoji: "🤘" },
  { id: "soul",       label: "Soul",               emoji: "🎶" },
  { id: "funk",       label: "Funk",               emoji: "🕺" },
  { id: "ambient",    label: "Ambient",            emoji: "🌌" },
  { id: "indie",      label: "Indie",              emoji: "🎙️" },
  { id: "folk",       label: "Folk",               emoji: "🪕" },
];

// ─── In-memory cache ────────────────────────────────────────────────────────

const cache = new Map<string, string[] | GenreConfigItem[]>();

// ─── Core fetch helper ──────────────────────────────────────────────────────

async function fetchOrSeed<T>(
  key: string,
  defaults: T[],
  cacheKey = key,
): Promise<T[]> {
  if (cache.has(cacheKey)) return cache.get(cacheKey) as T[];

  try {
    const ref = doc(db, "appConfig", key);
    const snap = await getDoc(ref);
    if (snap.exists()) {
      const data = snap.data() as { items: T[] };
      cache.set(cacheKey, data.items as any);
      return data.items;
    }
    // Seed defaults
    await setDoc(ref, { items: defaults, seededAt: Timestamp.now() });
    cache.set(cacheKey, defaults as any);
    return defaults;
  } catch {
    return defaults;
  }
}

// ─── Public API ─────────────────────────────────────────────────────────────

export async function getContributorRoles(): Promise<string[]> {
  return fetchOrSeed("contributorRoles", DEFAULT_CONTRIBUTOR_ROLES);
}

export async function getCreatorRoles(): Promise<string[]> {
  return fetchOrSeed("creatorRoles", DEFAULT_CREATOR_ROLES);
}

export async function getInstruments(): Promise<string[]> {
  return fetchOrSeed("instruments", DEFAULT_INSTRUMENTS);
}

export async function getCopyrightSocieties(): Promise<string[]> {
  return fetchOrSeed("copyrightSocieties", DEFAULT_COPYRIGHT_SOCIETIES);
}

export async function getPublishers(): Promise<string[]> {
  return fetchOrSeed("publishers", DEFAULT_PUBLISHERS);
}

export async function getSyncGenres(): Promise<string[]> {
  return fetchOrSeed("syncGenres", DEFAULT_SYNC_GENRES);
}

export async function getGenreConfig(): Promise<GenreConfigItem[]> {
  return fetchOrSeed<GenreConfigItem>("genres", DEFAULT_GENRES);
}

/**
 * Returns genres from the unified DB (artists + releases + published works),
 * mapped to canonical GenreConfigItem entries. Falls back to DEFAULT_GENRES.
 *
 * This is the single genre source used by all filter bars and radio selectors.
 */
export async function getGenresFromWorks(): Promise<GenreConfigItem[]> {
  const CACHE_KEY = "genresFromWorks";
  if (cache.has(CACHE_KEY)) return cache.get(CACHE_KEY) as GenreConfigItem[];

  try {
    const labelSet = new Set<string>();

    const addFromSnap = (snap: Awaited<ReturnType<typeof getDocs>>) => {
      snap.forEach(d => {
        const data = d.data() as Record<string, unknown>;
        const g = data.genre as string | undefined;
        const gs = data.genres as string[] | undefined;
        if (g) { const norm = normalizeGenre(g); if (norm) labelSet.add(norm); }
        if (Array.isArray(gs)) {
          gs.forEach((raw: string) => { const norm = normalizeGenre(raw); if (norm) labelSet.add(norm); });
        }
      });
    };

    const [worksSnap, artistsSnap, releasesSnap] = await Promise.all([
      getDocs(query(collection(db, "works"), where("status", "==", "Published"))),
      getDocs(collection(db, "artists")),
      getDocs(collection(db, "releases")),
    ]);

    addFromSnap(worksSnap);
    addFromSnap(artistsSnap);
    addFromSnap(releasesSnap);

    // Map to GenreConfigItem, preserving canonical emoji/id
    const result: GenreConfigItem[] = [];
    const sorted = Array.from(labelSet).filter(Boolean).sort();
    for (const label of sorted) {
      const canonical = GENRES.find(g => g.label === label);
      result.push(
        canonical
          ? { id: canonical.id, label: canonical.label, emoji: canonical.emoji }
          : { id: label.toLowerCase().replace(/[^a-z0-9]+/g, "-"), label, emoji: "🎵" }
      );
    }

    // If no data yet, fall back to full list so the UI isn't empty
    const final = result.length > 0 ? result : DEFAULT_GENRES;
    cache.set(CACHE_KEY, final as any);
    return final;
  } catch {
    return DEFAULT_GENRES;
  }
}

// ─── Write (admin) ──────────────────────────────────────────────────────────

export async function saveContributorRoles(items: string[]): Promise<void> {
  await setDoc(doc(db, "appConfig", "contributorRoles"), { items, updatedAt: Timestamp.now() });
  cache.set("contributorRoles", items);
}

export async function saveCreatorRoles(items: string[]): Promise<void> {
  await setDoc(doc(db, "appConfig", "creatorRoles"), { items, updatedAt: Timestamp.now() });
  cache.set("creatorRoles", items);
}

export async function saveInstruments(items: string[]): Promise<void> {
  await setDoc(doc(db, "appConfig", "instruments"), { items, updatedAt: Timestamp.now() });
  cache.set("instruments", items);
}

export async function saveCopyrightSocieties(items: string[]): Promise<void> {
  await setDoc(doc(db, "appConfig", "copyrightSocieties"), { items, updatedAt: Timestamp.now() });
  cache.set("copyrightSocieties", items);
}

export async function savePublishers(items: string[]): Promise<void> {
  await setDoc(doc(db, "appConfig", "publishers"), { items, updatedAt: Timestamp.now() });
  cache.set("publishers", items);
}

/** Append a single value to an appConfig string list (deduplicates). */
export async function appendToConfigList(key: string, value: string): Promise<void> {
  const current = await fetchOrSeed(key, []);
  const trimmed = value.trim();
  if (!trimmed || (current as string[]).some(v => v.toLowerCase() === trimmed.toLowerCase())) return;
  const updated = [...(current as string[]), trimmed].sort((a, b) => a.localeCompare(b));
  await setDoc(doc(db, "appConfig", key), { items: updated, updatedAt: Timestamp.now() });
  cache.set(key, updated);
}

export async function saveGenreConfig(items: GenreConfigItem[]): Promise<void> {
  await setDoc(doc(db, "appConfig", "genres"), { items, updatedAt: Timestamp.now() });
  cache.set("genres", items as any);
}

/** Force-clear the in-memory cache (call after admin saves). */
export function clearAppConfigCache(): void {
  cache.clear();
}

/** Overwrite an appConfig list and clear its cache entry. Use from admin to push updated defaults. */
export async function forceUpdateConfigList(key: string, items: string[]): Promise<void> {
  await setDoc(doc(db, "appConfig", key), { items, updatedAt: Timestamp.now() });
  cache.set(key, items);
}
